/* eslint-disable import-path/forbidden */
import React from 'react';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import _ from 'lodash';
import type { Types } from 'mongoose';
import UserTags from './UserTags';
import {
  AccountType,
  CustomerStatus,
  CustomerType,
  isLidar,
  PointCloudFormat,
  QAaaSStatus,
  WorkerStatus,
  getSanitizedReviewLevel,
} from '@scale/shared/defaults';
import { PipelineV3NewSuffix, TaskType } from '@scale/shared-types';
import RemotasksExternalLink from 'corp/components/UI/RemotasksExternalLink';
// FIXME: use MUI icon instead
import Chart from 'react-scale/components/icons/Chart';
import { REMOTASKS_FRONTEND_URL } from '@scale/frontend-lib/constants';
import DetailsTable from '../DetailsTable';
import LookupObjectLink from '../LookupObjectLink';
import ProjectOwners from './ProjectOwners';
import { TemporalNamespace } from '@scale/temporal-workflows/constants/namespaces';
/* Looking for feedback here; it seems like the above link lookup does not actually
press "enter" in the browser window in some cases, and only changes the address. Using this
Link actually takes you to that page. I didn't want to update all the instaces of Link to
this, however, which is why I have it imported as something weird */
import { Link as LoadingLink } from 'react-router-dom';
import { IProjectJson } from '@scale/shared/types/project';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'corp/components/SmartLink/SmartLink';
import { ProcessedLidarFrameJson } from '@scale/shared/types/corp/lookup';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import {
  TaskPipelineObject,
  PipelineV3HumanNodeCorpLookup,
  HydratedPipelineResource,
  HydratedPipelineExecution,
} from '@scale/shared/pipelinev3/lookup';
import {
  IRemoAdminContext,
  useRemoAdminCtx,
} from 'react-scale/components/remoAdmin/remoAdminContext';
import { DocumentModelErrorCode } from '@scale/shared/types/response/documentmodel';
import { PipelineV3HumanNodeStatus } from '@scale/shared/pipelinev3/humanNodeStatus';
import { IWorkLogJson } from '@scale/shared/corp/workLog';
import { QAOperationNodePopulatedOperationJson } from '@scale/shared/types/qaOperation/api';
import { QAOperationJson } from '@scale/shared/types/qaOperation/qaOperation';
import { QAOperationNodeType } from '@scale/shared/types/qaOperation/qaOperationNode';
import { durationToString } from 'react-scale/components/remoAdmin/qaOperation/createQAOperation/customInputs/helpers';
import { RemoAdminLookupLink } from 'react-scale/components/remoAdmin/remoAdminLookupLink';
import { LintOutcome, NormalLintResult } from '@scale/shared/types/linter';
import { ResponseLintersInfoTabs } from 'react-scale/corp/views/2DLintingPage/ResponseLintersInfoPage';
import { getLidarLiteLink } from 'react-scale/lib/helpers/redirectHelpers';
import { CrCOperationJson } from '@scale/shared/types/externalOperations/crcOperation';
import { HubstaffOperationJson } from '@scale/shared/types/externalOperations/hubstaffOperation';
import { TemporalCloudNamespaces } from '@scale/temporal-workflows/task-pipeline/framework/types';
import ConsoleLinkWithRedirect from 'react-scale/corp/components/Lookup/sections/ConsoleLinkWithRedirect';
import { Badge } from '@scale/scaleui-radix';
import { OTS_DELIVERY_STATUS_TO_COLOR } from 'react-scale/corp/components/OTS/ots-deliveries/otsDeliveriesConstants';
import {
  DeliveredBatchLookupDTO,
  OTSDeliveredBatchStatus,
  SubscriptionLookupDTO,
  OTSDeliveredTaskLookupDTO,
  OTSTaskLookupDTO,
  OTSUploadBatchLookupDTO,
} from '@scale/shared/ots';
import { ModelRequestAndResponseLookupDTO } from '@scale/customer-app-shared';

export function lookupLink(objectId: string, type?: INamedObjectTypes) {
  if (objectId) {
    return <LookupObjectLink objectId={objectId} type={type} />;
  }
  return;
}

function lookupLinkUntyped(objectId: string) {
  if (objectId) {
    return <LookupObjectLink objectId={objectId} />;
  }
  return;
}

function lookupLinks(objectIds: string[]) {
  return intersperse(objectIds.map(lookupLinkUntyped));
}

function labeledLookupLinks(objects: { id: string; label: string }[]) {
  return intersperse(
    objects
      .filter(({ id }) => !!id)
      .map(({ id, label }) => <LookupObjectLink key={id} objectId={id} label={label} />),
  );
}

function intersperse<T>(arr: T[]): (T | string)[] {
  return arr.reduce<(T | string)[]>((a, v) => [...a, v, ', '], []).slice(0, -1);
}

function temporalLink(params: {
  searchQuery: string;
  workflowType: string;
  temporalCloudNamespace: string | undefined;
}) {
  const { searchQuery, workflowType, temporalCloudNamespace } = params;
  let workflowId = searchQuery;
  if (['human', 'workflow', 'model'].includes(workflowType)) {
    workflowId += '-' + workflowType;
  }
  let base = 'https://temporal.internal.scale.com/namespaces/default';
  if (temporalCloudNamespace) {
    base = `https://cloud.temporal.io/namespaces/${temporalCloudNamespace}`;
  }
  return (
    <a
      href={`${base}/workflows?search=basic&query=WorkflowId%3D%22${workflowId}%22`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Workflow History
    </a>
  );
}

export function directTemporalLink(params: {
  workflowId: string;
  runId: string | undefined;
  text: string;
  temporalCloudNamespace: string | undefined;
}) {
  const { workflowId, runId, text, temporalCloudNamespace } = params;
  let base = 'https://temporal.internal.scale.com/namespaces/default';
  if (temporalCloudNamespace) {
    base = `https://cloud.temporal.io/namespaces/${temporalCloudNamespace}`;
  }
  const prefix = `${base}/workflows`;
  const href = runId ? `${prefix}/${workflowId}/${runId}` : `${prefix}/${workflowId}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
}

function linkWithText(text: string, link: string) {
  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
}

export function formatDateWithTz(dateString: string | Date, opts?: any): string {
  const date = new Date(dateString);
  let localTime;
  if (opts) {
    localTime = date.toLocaleString(undefined, opts);
  } else {
    localTime = date.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }
  const utcOffset = date.getTimezoneOffset() / 60; // in hours
  const utcDelta = `UTC${utcOffset >= 0 ? '-' : '+'}${Math.abs(utcOffset)}`;
  return `${localTime} (${utcDelta})`;
}

export function getEstimatedCompletionMinutes(jobSize: number) {
  let rateLimitPenalty: string | undefined = undefined;
  let estimatedCompletionMinutes: number | undefined = undefined;

  if (jobSize < 20_000) {
    rateLimitPenalty = 'None';
  } else if (jobSize >= 20_000 && jobSize < 60000) {
    rateLimitPenalty = `Moderate. Reason: Job size is: ${jobSize}`;
  } else if (jobSize >= 50_000 && jobSize < 100_000) {
    rateLimitPenalty = `High. Reason: Job size is: ${jobSize}`;
  } else {
    rateLimitPenalty = `Extreme. Reason: Job size is: ${jobSize}`;
  }
  estimatedCompletionMinutes = (jobSize / 20_000) * (jobSize / 2000) * 1.1;

  return {
    rateLimitPenalty,
    estimatedCompletionMinutes,
  };
}

function subtaskName(id: string, pipelineStages: $FIXME[]) {
  const relatedStage = pipelineStages.find(ps => ps.spawnedSubtask === id);
  const name = relatedStage?.nodeState?.nodeDef?.nodeName ?? 'subtask';
  return `${name.replace('_subtask', '')}-${id.slice(-8)}`;
}

// takes either a subtask or task
// eslint-disable-next-line complexity
function taskDetails(taskObject: $FIXME, remoAdminCtx: $FIXME) {
  const {
    id,
    type,
    status,
    task,
    subtasks,
    recalledSubtasks,
    attempts,
    pending_review: pendingReview,
    reviewLevel,
    batch,
    qaTask,
    qaSubtask,
    qaaasStatus,
    qaaasTask,
    qaFor,
    isInternalAuditPlus,
    auditPlus,
    created_at: createdAt,
    completed_at: completedAt,
    updatedAt,
    rehydrationDate,
    hasArchivedDocs,
    isTaskAttemptArchived,
    pipelineStages,
    lidarExperimentIds,
    response,
    quarantinedMetadata,
    rootTaskNodeStatus,
    rateLimitSeconds,
    anticipatedStartTime,
    pipelineV3ManualIntervention,
    nodeErrors,
    orchestrationErrorMessage,
    // Add back after backfills
    // canRedo,
    // canAudit,
    // canCreateDependentTasks,
    __t: objectType,
  } = taskObject;
  const user = (!remoAdminCtx.isRemoAdmin && taskObject.user) || undefined;
  const project = (!remoAdminCtx.hidePII && taskObject.project) || undefined;
  const permissionGroup =
    (!remoAdminCtx.hidePII && project && project.permissionGroup) || undefined;

  const lastAttempt: $FIXME = _.last(attempts);
  const merged: $FIXME = { ...lastAttempt, ...taskObject };
  const { assignedSession, claimed_until: claimedUntil, max_claim: maxClaim } = merged;

  let lidarAttachmentProcessed;
  if (isLidar(type) && subtasks && subtasks.length > 0) {
    lidarAttachmentProcessed = _.values(PointCloudFormat).includes(
      subtasks[0].params.pointCloudFormat,
    )
      ? 'yes'
      : 'no';
  }

  let actualStatus = status;
  if (pendingReview !== undefined && status === 'pending') {
    const reviewText = _.isNil(reviewLevel) ? 'yes' : `L${String(reviewLevel)}`;
    actualStatus += ` (in review: ${pendingReview ? reviewText : 'no'})`;
  } else if (
    status === 'error' &&
    response?.document_model_code === DocumentModelErrorCode.PasswordRequired
  ) {
    actualStatus = 'password required error';
  }

  let lastEvent;
  if (taskObject.eventHistory?.length > 0) {
    const event: $FIXME = _.last(taskObject.eventHistory);
    const previousStatus = event.previousState?.status || 'unknown status';
    const newStatus = event.newState?.status || 'unknown status';
    const reason = event.reason || 'unknown reason';
    lastEvent = `${previousStatus} -> ${newStatus} (${reason})`;
  }

  let claimData;
  if (taskObject.usesPipelineV3) {
    if (taskObject.currentWorkers?.length > 0) {
      claimData = {
        AssignedSession: taskObject.currentWorkers.map((w: string) =>
          lookupLink(w, INamedObjectTypes.User),
        ),
      };
    } else if (assignedSession) {
      // Audits are separate from pipeline v3, and also populate assignedSession
      claimData = {
        AuditAssignedSession: (assignedSession && lookupLink(assignedSession)) || 'None',
        AuditClaimedUntil: assignedSession && claimedUntil && formatDateWithTz(claimedUntil),
      };
    } else {
      claimData = {
        AssignedSession: 'No Active Claims',
      };
    }
  } else {
    claimData = {
      AssignedSession: (assignedSession && lookupLink(assignedSession)) || 'None',
      ClaimedUntil: assignedSession && claimedUntil && formatDateWithTz(claimedUntil),
      MaxClaim: assignedSession && maxClaim,
    };
  }

  const qaaasDetails: Record<string, React.ReactNode> = {};

  const advancedAuditDetails =
    taskObject.type === TaskType.Chat
      ? {
          'Advanced Audit': (
            <a href={`/corp/chat/advanced-audit/${id}`}>View advanced audit page</a>
          ),
        }
      : {};

  // the parent task will return a `qaaasTask`
  // and the audit task will return a `qaFor`
  if (qaaasTask || qaFor) {
    const disputeTaskId = qaaasTask ? qaaasTask.id : taskObject.id;
    const disputeLink = `/corp/dispute/${disputeTaskId}`;
    qaaasDetails.QAaaSTaskDispute = <a href={disputeLink}>QAaaS Dispute</a>;

    // This is a 2D only thing
    if (!isLidar(type) && type !== 'sensorfusion') {
      const viewLink = `${REMOTASKS_FRONTEND_URL}/internal_audit?taskId=${disputeTaskId}&perAnnotation=1`;
      qaaasDetails.QAaaSTaskView = <a href={viewLink}>QAaaS Audit View</a>;
    }
  }

  if (qaTask || qaSubtask || qaaasStatus) {
    const qaEntity = qaTask || qaSubtask;
    const qaaasStatusMsg = qaaasStatus
      ? `(${qaaasStatus === QAaaSStatus.AuditedInternal ? 'Completed' : 'Pending'})`
      : '';
    if (objectType === 'subtask') {
      qaaasDetails.QAaaSSubtask = (
        <span>
          {lookupLink(qaEntity)} {qaaasStatusMsg}
        </span>
      );
    } else if (objectType === 'task') {
      qaaasDetails.QAaaSTask = (
        <span>
          {lookupLink(qaEntity)} {qaaasStatusMsg}
        </span>
      );
    }
  }

  if (qaFor) {
    // technically this method renders subtasks and tasks, but want to tweak the
    // detail keys a bit based on that type
    if (objectType === 'subtask') {
      qaaasDetails.QAaaSForSubtask = <span>{lookupLink(qaFor, INamedObjectTypes.Subtask)}</span>;
    } else if (objectType === 'task') {
      qaaasDetails.QAaaSForTask = <span>{lookupLink(qaFor, INamedObjectTypes.Task)}</span>;
    }
  }

  if (qaFor && auditPlus?._id && (isLidar(type) || type === 'sensorfusion')) {
    const key = isInternalAuditPlus ? 'QIRHistoryViewer' : 'StandaloneAuditPlusHistoryViewer';
    const linkText = isInternalAuditPlus
      ? 'View QIR History'
      : 'View Audit+ History (without customer response)';

    qaaasDetails[key] = (
      <a
        href={getLidarLiteLink({
          auditId: auditPlus._id,
          entrypoint: 'diff-viewer',
        })}
      >
        {linkText}
      </a>
    );
    // TODO eventually support 2D
  }

  let ParentPipelineStage;
  if (taskObject.parentPipelineStage) {
    ParentPipelineStage = (
      <span>
        {taskObject.parentPipelineStage?.nodeState?.nodeDef?.nodeName}&nbsp;
        {lookupLink(taskObject.parentPipelineStage?._id)}
      </span>
    );
  }

  let AuditPlusInfo;
  let LinkToAuditTask;
  let LinkToParentTask;
  if (auditPlus) {
    AuditPlusInfo = <span>{lookupLink(auditPlus._id)}</span>;
    if (auditPlus.parentTask._id === id) {
      LinkToAuditTask = <span>{lookupLink(auditPlus.auditTask._id, INamedObjectTypes.Task)}</span>;
    } else if (auditPlus.auditTask._id === id) {
      LinkToParentTask = (
        <span>{lookupLink(auditPlus.parentTask._id, INamedObjectTypes.Task)}</span>
      );
    }
  }

  let LidarExperiments;
  if (lidarExperimentIds && lidarExperimentIds.length) {
    LidarExperiments = (
      <span>
        {labeledLookupLinks(
          lidarExperimentIds.map((ex: Types.ObjectId) => {
            const id = ex.toString();
            return { id, label: id };
          }),
        )}
      </span>
    );
  }

  const getCurrentLintResults = (attempts: any) => {
    if (!attempts || !attempts.length) return undefined;
    const lastAttempt = attempts[attempts.length - 1];
    const results = lastAttempt.confidenceScores;
    const verdict = results?.lintVerdict;
    let verdictMessage = verdict;

    if (results && results.lintResults && results.lintResults[verdict]) {
      verdictMessage = `${verdict} (${(results?.lintResults[verdict] as NormalLintResult[])
        .filter(linter => linter.lintOutcome === LintOutcome.Fail)
        .map(linter => linter.name)
        .join(' - ')})`;
    }

    return {
      verdictMessage,
      project: lastAttempt.project,
      subtask: lastAttempt.subtask,
    };
  };

  const currentLintResults =
    !_.isNil(getCurrentLintResults(attempts)) && getCurrentLintResults(attempts);

  const originalAttemptId = taskObject.params?.metadata?.original_attempt;
  const OriginalAttempt = originalAttemptId && (
    <span>{lookupLink(originalAttemptId, INamedObjectTypes.TaskAttempt)}</span>
  );

  const originalAttempterId = taskObject.params?.metadata?.original_attempter;
  const OriginalAttempter = originalAttempterId && (
    <span>{lookupLink(originalAttempterId, INamedObjectTypes.User)}</span>
  );

  return {
    Processed: lidarAttachmentProcessed,
    Customer: user && user._id && (
      <span>
        {user.email} {lookupLink(user._id, INamedObjectTypes.User)}
      </span>
    ),
    Project: project && (
      <span>
        {project.name}{' '}
        {!remoAdminCtx.isRemoAdmin
          ? lookupLink(project._id, INamedObjectTypes.Project)
          : `| ${project._id}`}
      </span>
    ),
    PermissionGroup: permissionGroup && (
      <span>{lookupLink(permissionGroup, INamedObjectTypes.PermissionGroup)}</span>
    ),
    Batch: batch && (
      <span>
        {batch.name} {lookupLink(batch._id)}
      </span>
    ),
    Type: type,
    Status: actualStatus,
    CurrentErrors: nodeErrors?.length ? nodeErrors : undefined,
    LastEvent: lastEvent,
    CreatedAt: formatDateWithTz(createdAt),
    CompletedAt: completedAt ? formatDateWithTz(completedAt) : undefined,
    UpdatedAt: formatDateWithTz(updatedAt),
    RehydrationDate: rehydrationDate && formatDateWithTz(rehydrationDate),
    HasArchivedDocs: hasArchivedDocs?.toString(),
    TaskAttemptArchived: isTaskAttemptArchived ? isTaskAttemptArchived?.toString() : undefined,
    // Add these back after backfills
    // CanAudit: canAudit?.toString(),
    // CanRedo: canRedo?.toString(),
    // CanCreateDependentTasks: canCreateDependentTasks?.toString(),
    Task: task && lookupLink(task._id, INamedObjectTypes.Task),
    ManualInterventionInPipelineV3:
      pipelineV3ManualIntervention &&
      lookupLink(pipelineV3ManualIntervention.id, INamedObjectTypes.PipelineV3ManualIntervention),
    OriginalAttempt,
    OriginalAttempter,
    ShadowSubtask:
      subtasks &&
      subtasks[0]?.params?.shadowSubtask &&
      lookupLink(subtasks[0].params.shadowSubtask, INamedObjectTypes.Subtask),
    LidarExperiments,
    ParentPipelineStage,
    AuditPlusInfo,
    LinkToAuditTask,
    ...advancedAuditDetails,
    LinkToParentTask,
    ...qaaasDetails,
    ...claimData,
    ...(taskObject.usesPipelineV3
      ? {
          PipelineStatus: rootTaskNodeStatus,
          ReviewLevel: taskObject.reviewLevel,
          RootTaskNode: taskObject.rootTaskNode && lookupLink(taskObject.rootTaskNode),
          HumanNodeStatus: taskObject.humanNodeStatus,
          QuarantinedMetadata: quarantinedMetadata,
          PastRootTaskNodes:
            taskObject.pastRootTaskNodes && taskObject.pastRootTaskNodes.length
              ? taskObject.pastRootTaskNodes.map((id: string) => lookupLink(id))
              : undefined,
          RateLimitSeconds: rateLimitSeconds,
          AnticipatedStartTime: anticipatedStartTime
            ? formatDateWithTz(anticipatedStartTime)
            : undefined,
          PipelineErrors: orchestrationErrorMessage,
        }
      : {
          Subtasks:
            subtasks &&
            labeledLookupLinks(
              subtasks.map((s: $FIXME) => {
                if (!s) return { id: 'subtask_archived', label: 'subtask archived' };
                const id = s?._id || String(s);
                return { id, label: subtaskName(id, pipelineStages) };
              }),
            ),
          RecalledSubtasks:
            recalledSubtasks &&
            labeledLookupLinks(
              recalledSubtasks.map((rs: $FIXME) => {
                const id = rs._id || String(rs);
                return { id, label: subtaskName(id, pipelineStages) };
              }),
            ),
          CurrentLintResults: currentLintResults && (
            <span>
              <Link
                href={`/response_linters_info?project=${currentLintResults.project}&subtask=${currentLintResults.subtask}&tab=${ResponseLintersInfoTabs.Results}`}
                target="_blank"
              >
                {currentLintResults.verdictMessage}
              </Link>
            </span>
          ),
        }),
    'Worker Skills': taskObject.workerSkillNames?.join(', '),
    Specializations: taskObject.specializations?.join(', '),
  };
}

function getRole(
  accountType: AccountType,
  customerStatus: CustomerStatus,
  workerStatus: WorkerStatus,
) {
  if (accountType === AccountType.User) {
    switch (customerStatus) {
      case CustomerStatus.Disabled:
        return (
          <span>
            Customer <strong>(DISABLED)</strong>
          </span>
        );
      case CustomerStatus.Locked:
        return (
          <span>
            Customer <strong>(LOCKED)</strong>
          </span>
        );
      default:
        return <span>Customer</span>;
    }
  }
  if ([AccountType.Worker, AccountType.Expert].includes(accountType)) {
    switch (workerStatus) {
      case WorkerStatus.Banned:
        return (
          <span>
            Tasker <strong>(BANNED)</strong>
          </span>
        );
      case WorkerStatus.Locked:
        return (
          <span>
            Tasker <strong>(LOCKED)</strong>
          </span>
        );
      default:
        return <span>Tasker</span>;
    }
  }
}

function userDetails(userObject: $FIXME) {
  const {
    email,
    workerStatus,
    accountType,
    customerType,
    customerStatus,
    lastOnlineDatetime,
    team,
    accessRoles, // only populated for corp users
    teamRole, // only relevant for customers
    tieredAssignedProjects,
    projectPermissions,
    projectNames,
    activeWorkerTeamId,
    activeWorkerTeamName,
    activeWorkerTeamAssignments,
    // possible contexts: lookup(user), explain queue
    tags,
    _id,
    customerCodeNames,
    bootcampStatus,
    isRemoAdmin,
    disableRemoAdminForCustomer,
  } = userObject;

  const role = getRole(accountType, customerStatus, workerStatus);

  let lastOnline;
  if (lastOnlineDatetime) {
    lastOnline = (
      <span>
        {moment.utc(lastOnlineDatetime).format()}
        &nbsp;<strong>({moment.utc(lastOnlineDatetime).fromNow()})</strong>
      </span>
    );
  }

  const bootcampStatusDetails = {
    // this is captilaized since it shows up in the frontend as text
    BootcampRole:
      bootcampStatus && bootcampStatus.role ? (
        <span>{bootcampStatus.role}</span>
      ) : (
        <span> No Active Bootcamp Role</span>
      ),
    CurrentCohort:
      bootcampStatus && bootcampStatus.currentCohort ? (
        <span>{bootcampStatus.currentCohort}</span>
      ) : (
        <span> No Current Cohort</span>
      ),
    IsActive:
      bootcampStatus && bootcampStatus.IsActive ? (
        <span>{bootcampStatus.IsActive}</span>
      ) : (
        <span> No isActive Logged</span>
      ),
  };

  const details: Record<string, React.ReactNode> = {
    Role: role,
    Email: email,
    LastOnline: lastOnline,
    Tags: (
      <div style={{ maxWidth: '400px' }}>
        <UserTags userId={_id} tags={tags} />
      </div>
    ),
  };

  if (accessRoles && accessRoles.length > 0)
    details.CorpAccessRoles = _.castArray(accessRoles).join(', ');

  if (accountType === AccountType.User) {
    details.CustomerType = customerType;

    details.TeamRole = teamRole;

    details.DisableRemoAdmin = disableRemoAdminForCustomer;
  }

  if ([AccountType.Worker, AccountType.Expert].includes(accountType)) {
    details.IsRemoAdmin = isRemoAdmin ? 'Yes' : 'No';
    _.assign(details, bootcampStatusDetails);

    if (activeWorkerTeamId) {
      const teamAssignedProjects: any = { Primary: [], Secondary: [] };
      for (const assign of activeWorkerTeamAssignments) {
        teamAssignedProjects[assign.tier] = [...teamAssignedProjects[assign.tier], assign];
      }
      details.ActiveWorkerTeam = (
        <>
          <a href={`/corp/lookup/${activeWorkerTeamId}`}>{activeWorkerTeamName}</a>
          <table style={{ marginLeft: '10px' }}>
            <tbody>
              {_.map(teamAssignedProjects, (projects, tier) => (
                <>
                  <tr style={{ height: '8px' }}></tr>
                  <tr key={tier}>
                    <td style={{ paddingRight: '10px' }}>Team {tier}</td>
                    {projects.length === 0
                      ? 'None'
                      : projects.map((p: $FIXME) => (
                          <span key={p.projectId}>
                            {p.projectName}{' '}
                            {_.isNil(p.reviewLevel) ? '(*) ' : `(L${p.reviewLevel}) `}
                            <LookupObjectLink objectId={p.projectId} />
                          </span>
                        ))}
                  </tr>
                </>
              ))}
            </tbody>
          </table>
        </>
      );
    }
    details.IndividualAssignments = (
      <table style={{ marginLeft: '10px' }}>
        <tbody>
          {_.map(tieredAssignedProjects, (projects, tier) => (
            <>
              <tr style={{ height: '8px' }}></tr>
              <tr key={tier}>
                <td style={{ paddingRight: '10px' }}>{tier}</td>
                {projects.length === 0
                  ? 'None'
                  : projects.map((p: { _id: any }) => {
                      const projectId = p._id || p;
                      return (
                        <span key={projectId}>
                          {projectNames[projectId]}
                          {' ('}
                          {projectId in projectPermissions
                            ? `L${projectPermissions[projectId]}`
                            : 'No Project Permissions'}
                          {') '}
                          <LookupObjectLink objectId={projectId} />
                        </span>
                      );
                    })}
              </tr>
            </>
          ))}
        </tbody>
      </table>
    );
  } else if (customerType !== CustomerType.SelfService && accountType === AccountType.User) {
    details.Team = team || 'Root account for team';
  }

  if (customerCodeNames && customerCodeNames.length > 0)
    details.CustomerCodeNames = customerCodeNames.join(', ');

  return details;
}

function taskReviewDetails(taskReviewObject: $FIXME) {
  const { subtask, taskAttempt, reviewOutcome, actualOutcome, payable } = taskReviewObject;
  return {
    Subtask: lookupLink((subtask && subtask._id) || subtask, INamedObjectTypes.Subtask),
    Attempt: lookupLink(
      (taskAttempt && taskAttempt._id) || taskAttempt,
      INamedObjectTypes.TaskAttempt,
    ),
    Outcome: actualOutcome || reviewOutcome,
    Payable: lookupLink(payable),
  };
}

function taskAttemptDetails(taskAttemptObject: $FIXME) {
  const {
    project,
    taskType,
    task,
    subtask,
    reviews,
    reviewStatus,
    attemptedAtReviewLevel,
    actualReviewStatus,
    attemptedAt,
    attemptedBy,
    payable,
  } = taskAttemptObject;

  const formatReviewLevel = getSanitizedReviewLevel(attemptedAtReviewLevel);

  return {
    Type: taskType,
    Project: project && lookupLink(project, INamedObjectTypes.Project),
    Task: task && lookupLink(task._id, INamedObjectTypes.Task),
    Subtask: subtask && lookupLink(subtask._id, INamedObjectTypes.Subtask),
    Reviews: reviews && lookupLinks(reviews.map((r: $FIXME) => r._id)),
    'Attempted at': attemptedAt,
    'Attempted by': lookupLink(attemptedBy, INamedObjectTypes.User),
    'Review status': taskAttemptObject.usesPipelineV3
      ? reviewStatus
      : actualReviewStatus || reviewStatus,
    'Attempted at Review level': formatReviewLevel,
    Payable: lookupLink(payable),
  };
}

function projectDetails(projectObject: $FIXME) {
  const {
    taskType,
    taskCustomer: user,
    permissionGroup,
    name,
    _id,
    qaaasProject,
    qaaasFor,
    projectMetricsEnabled,
    productVertical,
    createdAt,
    updatedAt,
    owners,
  } = projectObject;
  return {
    Name: name,
    ProductVertical: (
      <span>
        {productVertical ?? 'UNSPECIFIED'}{' '}
        <a
          href={`/corp/nice_configs/project/${_id}#productvertical`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Edit
        </a>
      </span>
    ),
    Customer: user && (
      <span>
        {user.email} {lookupLink(user._id, INamedObjectTypes.User)}
      </span>
    ),
    Type: taskType,
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
    PermissionGroup: permissionGroup && (
      <span>
        {permissionGroup.name} {lookupLink(permissionGroup._id, INamedObjectTypes.PermissionGroup)}
      </span>
    ),
    ProjectOwners: owners && (
      <ProjectOwners owners={owners.owners} corpPermissionRoleId={owners.ownerPermissionRoleId} />
    ),
    SampleTask: (
      <RemotasksExternalLink path={`/tasks?force=1&project=${_id}`}>
        Open Sample Task
      </RemotasksExternalLink>
    ),
    QAaaSProject: lookupLink(qaaasProject, INamedObjectTypes.Project),
    QAaaSFor: lookupLink(qaaasFor, INamedObjectTypes.Project),
    DeliveryPortal: (
      <LoadingLink to={`/qm/queue/${_id}`}>Open Project in Delivery Portal</LoadingLink>
    ),
    ProjectMetricsEnabled: projectMetricsEnabled && (
      <a
        href={`/corp/projects/metrics?projectIds=${_id}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <Chart className="chart-icon" width={24} height={24} fill="currentColor" />
        Project Metrics
      </a>
    ),
    QuickLinters: quickLinterTaskTypes.includes(taskType) && (
      <LoadingLink to={`/qm/queue/${_id}/quick-linters`}>Quick Linters (beta)</LoadingLink>
    ),
  };
}

const quickLinterTaskTypes: TaskType[] = [
  TaskType.DocumentTranscription,
  TaskType.NamedEntityRecognition,
];

function permissionEntryDetails(taskPermissionEntry: $FIXME) {
  const { canReview, reviewLevel, taskType, taskCustomer, permissionGroup } = taskPermissionEntry;

  return {
    TaskType: taskType,
    TaskCustomer: taskCustomer && (
      <span>
        {taskCustomer.email} {lookupLink(taskCustomer._id, INamedObjectTypes.User)}
      </span>
    ),
    PermissionGroup: permissionGroup && (
      <span>
        {lookupLink(permissionGroup._id, INamedObjectTypes.PermissionGroup)} (project:{' '}
        {lookupLinks(permissionGroup.projects)})
      </span>
    ),
    CanReview: canReview ? 'Yes' : 'No',
    ReviewLevel: reviewLevel,
  };
}

function permissionGroupDetails(taskPermissionGroup: $FIXME) {
  const { name, title, taskType, taskCustomer, projects } = taskPermissionGroup;

  return {
    Type: taskType,
    Customer: taskCustomer && (
      <span>
        {taskCustomer.email} {lookupLink(taskCustomer._id, INamedObjectTypes.User)}
      </span>
    ),
    Name: name,
    Title: title,
    Projects: projects && (
      <span>
        {projects.map((p: IProjectJson) => (
          <SmartLink key={p._id} type={INamedObjectTypes.Project} id={p._id} />
        ))}
      </span>
    ),
  };
}

function trainingScenarioDetails(trainingScenario: $FIXME) {
  const {
    params: { customerType = undefined, taskId: templateId = undefined } = {},
    taskCustomer,
    project,
    taskType,
    trainingType,
    createdBy,
    createdAt,
    updatedAt,
    benchmark,
  } = trainingScenario;

  return {
    Customer: taskCustomer && (
      <span>
        {taskCustomer.email} {lookupLink(taskCustomer._id, INamedObjectTypes.User)}
      </span>
    ),
    Project: project && (
      <span>
        {project.name} {lookupLink(project._id, INamedObjectTypes.Project)}
      </span>
    ),
    TaskType: taskType,
    CustomerType: customerType,
    Template: templateId,
    CreatedBy: createdBy,
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
    TrainingType: trainingType,
    BenchmarkStatus: benchmark.status,
    BenchmarkType: benchmark.type,
    InstructiveBenchmark: benchmark?.isInstructiveBenchmark.toString(),
    AttempterBenchmark: benchmark?.isAttempterBenchmark.toString(),
    ReviewLevels: '[' + benchmark?.reviewLevels.join(', ') + ']',
  };
}

function getInstructionItemAccuracyObject(
  graderResponse: $FIXME,
  trainingAttemptInstructionItems: $FIXME,
) {
  if (!graderResponse?.perInstructionItemAccuracies || !trainingAttemptInstructionItems) {
    return {};
  }

  const instructionItemIDToName: Record<string, string> = {};
  for (const instructionItem of trainingAttemptInstructionItems) {
    instructionItemIDToName[instructionItem._id] = instructionItem.name;
  }

  const output: Record<string, string> = {};
  for (const [key, arr] of Object.entries<$FIXME>(graderResponse.perInstructionItemAccuracies)) {
    if (arr.length) {
      const avgAccuracy =
        arr.reduce((sum: number, ii: $FIXME) => sum + (ii.accuracy || 0), 0) / arr.length;
      output[_.get(instructionItemIDToName, key, key) + ' Accuracy'] =
        accuracyToString(avgAccuracy);
    }
  }
  return output;
}

function accuracyToString(accuracy: number): string {
  let roundedAccuracy: number | string = accuracy * 100;
  if (Math.round(roundedAccuracy) !== roundedAccuracy) {
    roundedAccuracy = roundedAccuracy.toFixed(2);
  }
  return String(roundedAccuracy) + '%';
}

function trainingAttemptDetails(trainingAttempt: $FIXME) {
  const {
    taskType,
    trainingScenario,
    project,
    createdAt,
    submittedAt,
    worker,
    accuracy,
    graderResponse,
    trainingAttemptInstructionItems,
    timeSpentSecs,
    skipped,
    payable,
  } = trainingAttempt;

  return {
    TaskType: taskType,
    TrainingScenario: lookupLink(trainingScenario._id, INamedObjectTypes.TrainingScenario),
    Project: lookupLink(project, INamedObjectTypes.Project),
    CreatedAt: formatDateWithTz(createdAt),
    SubmittedAt: submittedAt,
    Worker: <SmartLink type={INamedObjectTypes.User} id={worker} />,

    ...(skipped
      ? {}
      : {
          OverallBenchmarkAccuracy: accuracyToString(accuracy),
          TimeSpent: moment.duration(timeSpentSecs, 'seconds').humanize(),
        }),
    ...getInstructionItemAccuracyObject(graderResponse, trainingAttemptInstructionItems),
    Payable: lookupLink(payable),
  };
}

function courseDetails(course: $FIXME) {
  const { title, taskType, createdAt, updatedAt, name, createdBy } = course;

  return {
    TaskType: taskType,
    Name: name,
    Title: title,
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
    CreatedBy: createdBy && createdBy.email,
  };
}

function customerAuditDetails(audit: $FIXME) {
  const { customer, project, taskType, task, subtask, reviewedBy, createdAt, updatedAt } = audit;

  return {
    Customer: customer && customer._id && (
      <span>
        {customer.email} {lookupLink(customer._id, INamedObjectTypes.User)}
      </span>
    ),
    Project: project && (
      <span>
        {project.name} {lookupLink(project._id, INamedObjectTypes.Project)}
      </span>
    ),
    Type: taskType,
    Task: task && lookupLink(task._id, INamedObjectTypes.Task),
    Subtask: subtask && lookupLink(subtask._id, INamedObjectTypes.Subtask),
    Reviewer: reviewedBy && (
      <span>
        {reviewedBy.email} {lookupLink(reviewedBy._id, INamedObjectTypes.User)}
      </span>
    ),
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
  };
}

const DEFAULT_TAG_PAY_MULTIPLIER = 1;

const TagPayMultiplier = (props: $FIXME) => {
  const originalPayMultiplier = props.tag.metadata?.payMultiplier ?? DEFAULT_TAG_PAY_MULTIPLIER;
  if (originalPayMultiplier === DEFAULT_TAG_PAY_MULTIPLIER) return <strong>None</strong>;

  return <span>{originalPayMultiplier}</span>;
};

function tagDetails(tag: $FIXME) {
  const lastEventTime = moment(tag.lastEvent.time);
  const fields: Record<string, $FIXME> = {
    Tagged: tag.taggedCount,
    LastEvent: (
      <React.Fragment>
        {`${tag.lastEvent.description} by ${tag.lastEvent.sourceUserEmail} `}
        <span
          data-tip={`${lastEventTime.utc().format('MMM D, h:mm a')} (UTC)`}
          data-for={lastEventTime.toString()}
        >
          {lastEventTime.fromNow()}
        </span>
        <ReactTooltip id={lastEventTime.toString()} />
      </React.Fragment>
    ),
  };
  if ((tag?.metadata?.payMultiplier ?? DEFAULT_TAG_PAY_MULTIPLIER) !== DEFAULT_TAG_PAY_MULTIPLIER) {
    fields.UserPayMultiplier = <TagPayMultiplier tag={tag} />;
  }
  return fields;
}

function batchDetails(batch: $FIXME) {
  const { project, taskCustomer, _id } = batch;
  return {
    Customer: taskCustomer && (
      <span>
        {taskCustomer.email} {lookupLink(taskCustomer._id, INamedObjectTypes.User)}
      </span>
    ),
    Project: project && (
      <span>
        {project.name} {lookupLink(project._id, INamedObjectTypes.Project)}
      </span>
    ),
    'GenAI Console': (
      <ConsoleLinkWithRedirect relativeHref={`/batches/${_id}?spoof=${taskCustomer._id}`} />
    ),
  };
}

function chatDeliveryBatchDetails(batch: $FIXME) {
  const { customerId, _id } = batch;
  return {
    'GenAI Console': (
      <ConsoleLinkWithRedirect relativeHref={`/deliveries/${_id}?spoof=${customerId}`} />
    ),
  };
}

function pipelineStages(pipelineStage: $FIXME) {
  const {
    project,
    task,
    containerSubtask,
    createdAt,
    pipelineName,
    nodeState,
    status,
    containerPipelineStage,
    childStages,
  } = pipelineStage;
  const details: Record<string, React.ReactNode> = {
    Project: project && (
      <span>
        {project.name} {lookupLink(project._id, INamedObjectTypes.Project)}
      </span>
    ),
    Task: task && lookupLink(task._id, INamedObjectTypes.Task),
    ContainerSubtask: containerSubtask && lookupLink(containerSubtask, INamedObjectTypes.Subtask),
    CreatedAt: formatDateWithTz(createdAt),
    PipelineName: pipelineName,
    NodeName: nodeState.nodeDef.nodeName,
    NodeType: nodeState.nodeType,
    IsSubpipelineStage: containerPipelineStage && <span>Yes</span>,
    ContainerPipelineStage: containerPipelineStage && lookupLink(containerPipelineStage),
    Status: status,
  };

  if (
    nodeState.nodeDef.nodeName === 'corp_flagged' &&
    nodeState.transitionReason === 'has_lint_escalation'
  ) {
    const lintResults =
      childStages[childStages.length - 1]?.completionOpts.submissionProcessingOutput.lintResults;
    const lintVerdict = lintResults?.lintVerdict;
    const currentLintResults = (lintResults?.lintResults[lintVerdict] as NormalLintResult[])
      ?.filter(linter => linter.lintOutcome === LintOutcome.Fail)
      .map(linter => linter.name)
      .join(' - ');

    const currentLintResultsMessage = `${lintVerdict} (${currentLintResults})`;

    details.CurrentLintResults = (
      <span>
        <Link
          href={`/response_linters_info?project=${project._id}&subtask=${containerSubtask}&tab=results`}
          target="_blank"
        >
          {currentLintResultsMessage}
        </Link>
      </span>
    );
  }

  if (nodeState?.nodeType === 'step_fn') {
    const stateMachineType = nodeState.nodeDef?.stateMachineType;
    const stateMachineName = nodeState.stateMachineName;
    const executionArn = nodeState.executionArn;
    const href = executionArn
      ? `/lookup/${executionArn}`
      : `/dct/check-stepfunction-status?sfnId=${stateMachineName}&sfnType=${stateMachineType}`;
    details.CheckStateMachineStatus = <LoadingLink to={href}>Check SFN Status</LoadingLink>;
  }

  return details;
}

function corpPermissionRoleDetails(corpPermissionRole: $FIXME) {
  const { name, type, permissionLevel, createdAt, updatedAt } = corpPermissionRole;
  return {
    Name: name,
    PermissionType: type,
    PermissionLevel: permissionLevel,
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
  };
}

function contractDetails(contract: $FIXME) {
  const {
    salesOrder,
    contractVersion,
    customer,
    totalValue,
    onboardingValue,
    parentContract,
    amendedContract,
    url,
    targetStartDate,
    targetEndDate,
    overriddenStartDate,
    extendedContractEndDate,
    comment,
    description,
  } = contract;

  const retObj: Record<string, React.ReactNode> = {
    'Sales Order': salesOrder,
    'Contract Version': contractVersion,
    Customer: customer ? (
      <span>
        {customer.email} {lookupLink(customer._id)}
      </span>
    ) : (
      <span>N/A</span>
    ),
    Document: <a href={url}>PDF</a>,
    'Total Value': new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      totalValue,
    ),
    Comment: comment,
    Description: description,
  };

  if (targetStartDate) {
    retObj['Target Start Date'] = formatDateWithTz(targetStartDate);
  }
  if (targetEndDate) {
    retObj['Target End Date'] = formatDateWithTz(targetEndDate);
  }

  if (overriddenStartDate) {
    retObj['Target Overridden Start Date'] = formatDateWithTz(overriddenStartDate);
  }

  if (extendedContractEndDate) {
    retObj['Target Extended End Date'] = formatDateWithTz(extendedContractEndDate);
  }

  if (onboardingValue) {
    retObj['Onboarding Value'] = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(onboardingValue);
  }

  if (parentContract) {
    retObj['Parent Contract'] = lookupLink(parentContract);
  }
  if (amendedContract) {
    retObj['Amended Contract'] = lookupLink(amendedContract);
  }

  return retObj;
}

function instructionItemDetails(instructionItem: $FIXME) {
  const { name, version, fromCourses, examples, createdAt, lastUpdated } = instructionItem;
  return {
    Name: name,
    CourseCount: _.uniq(fromCourses).length,
    ExampleCount: (examples && examples.length) || 0,
    Version: version,
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: lastUpdated,
  };
}

function adminActionDetails(adminAction: $FIXME) {
  const { performedBy, performedAt, reason, type, commandName } = adminAction;
  const result: Record<string, React.ReactNode> = {
    PerformedBy: performedBy?.email ?? 'system',
    PerformedAt: performedAt,
    Reason: reason,
    Type: type,
  };
  if (type === 'lookup_command') {
    result.Command = commandName;
  }
  return result;
}

function auditPlusDetails(auditPlus: $FIXME) {
  const { parentTask, auditTask, status } = auditPlus;
  const result = {
    ParentTask: parentTask && lookupLink(parentTask, INamedObjectTypes.Task),
    AuditTask: auditTask && lookupLink(auditTask, INamedObjectTypes.Task),
    Status: status,
  };
  return result;
}

function ontologyDetails(ontology: $FIXME) {
  const { name, project } = ontology;
  const result = {
    OntologyName: <>{name}</>,
    Project: project && lookupLink(project, INamedObjectTypes.Project),
  };
  return result;
}

function cuboidPathDetails(cuboidPath: $FIXME) {
  const { scene, props } = cuboidPath;
  const result = {
    Scene: scene && lookupLink(scene),
    Label: props?.static?.label,
  };
  return result;
}

function corpJobDetails(corpJob: $FIXME) {
  const {
    createdAt,
    finishedAt,
    name,
    status,
    user,
    metadata,
    jsonOutput,
    jsonInput,
    temporalRunId,
    temporalWorkflowId,
    errorReason,
    isolated,
    temporalCloudRunId,
    temporalCloudWorkflowId,
  } = corpJob;
  const sfnExecutionName =
    metadata?.stepFunctionArn && metadata.stepFunctionArn.split(':').slice(-1)[0];
  let RateLimitPenalty: string | undefined = undefined;
  let EstimatedCompletionTime: string | undefined = undefined;
  if (temporalWorkflowId && temporalWorkflowId.includes('bulk')) {
    const jobSize = jsonInput?.entityIds?.length;
    if (jobSize) {
      const { rateLimitPenalty, estimatedCompletionMinutes } =
        getEstimatedCompletionMinutes(jobSize);
      RateLimitPenalty = rateLimitPenalty;
      EstimatedCompletionTime = moment(createdAt)
        .add(estimatedCompletionMinutes, 'minutes')
        .format();
    }
  }
  const result = {
    CorpJobName: (
      <>
        {name}
        <RemoSafeLoadingLink to={`/jobs?jobName=${name}`}>
          {' '}
          See recent runs of this job
        </RemoSafeLoadingLink>
      </>
    ),
    Warning: isolated
      ? linkWithText(
          `CorpJob running on isolation queue`,
          `https://scale.atlassian.net/wiki/spaces/EPD/pages/1204125698/Running+PipelineV3+BulkJobs#Mitigation-of-%E2%80%9CBulk-Job-Spam%E2%80%9D`,
        )
      : undefined,
    CreatedAt: formatDateWithTz(createdAt),
    FinishedAt: formatDateWithTz(finishedAt),
    RateLimitPenalty,
    EstimatedCompletionTime: EstimatedCompletionTime && formatDateWithTz(EstimatedCompletionTime),
    CreatedBy: (
      <>
        {user.email}
        <RemoSafeLoadingLink to={`/jobs?performedBy=${user.email}`}>
          {' '}
          See recent jobs by user
        </RemoSafeLoadingLink>
      </>
    ),
    Status: status,
    ErrorReason: errorReason,
    StepFunction: metadata?.stepFunctionArn && (
      <RemoSafeLoadingLink to={`/lookup/${metadata.stepFunctionArn}`}>
        Execution
      </RemoSafeLoadingLink>
    ),
    OutputMessage: jsonOutput?.message ?? 'n/a',
    DatadogSFNLogs: sfnExecutionName && (
      <a
        href={`https://app.datadoghq.com/logs?query=@context.sfnExecution.execution.name:${sfnExecutionName}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        SFN Logs
      </a>
    ),
    TemporalLegacyWorkflow:
      temporalRunId && !temporalWorkflowId.endsWith(PipelineV3NewSuffix)
        ? directTemporalLink({
            workflowId: temporalWorkflowId,
            runId: temporalRunId,
            text: 'Workflow History',
            temporalCloudNamespace: TemporalNamespace.LegacyCloud,
          })
        : undefined,
    TemporalCloudWorkflow:
      (temporalRunId && temporalWorkflowId.endsWith(PipelineV3NewSuffix)
        ? directTemporalLink({
            workflowId: temporalWorkflowId,
            runId: temporalRunId,
            text: 'Workflow History',
            temporalCloudNamespace: TemporalNamespace.TaskPipeline,
          })
        : undefined) ||
      (temporalCloudRunId && temporalCloudWorkflowId
        ? directTemporalLink({
            workflowId: temporalCloudWorkflowId,
            runId: temporalCloudRunId,
            text: 'Workflow History',
            temporalCloudNamespace: TemporalNamespace.TaskPipeline,
          })
        : undefined),
  };
  return result;
}

const RemoSafeLoadingLink: React.FunctionComponent<{ to: string; children?: React.ReactNode }> = ({
  to,
  children,
}) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  if (isRemoAdmin) return <></>;
  return <LoadingLink to={to}>{children}</LoadingLink>;
};

function testProjectDetails(testProject: $FIXME): Record<string, React.ReactNode> | null {
  const user = testProject.taskCustomer;
  return {
    Name: testProject.name,
    Customer: user && (
      <span>
        {user.email} {lookupLink(user._id, INamedObjectTypes.User)}
      </span>
    ),
  };
}

function plfDetails(plf: ProcessedLidarFrameJson): Record<string, React.ReactNode> | null {
  // processed lidar frames
  return {
    Task: plf?.task && lookupLink(plf.task, INamedObjectTypes.Task),
    FrameNumber: plf?.frameNumber,
    ProcessedAt: plf?.processedAt && formatDateWithTz(plf.processedAt),
    UpdatedAt: plf?.updatedAt && formatDateWithTz(plf.updatedAt),
    // @ts-expect-error @types/react@18 $FIXME Date is not a ReactNode
    ArchiveDate: plf?.archiveDate,
    // @ts-expect-error @types/react@18 $FIXME BinaryPointCloudUrls is not a ReactNode
    BinaryPointCloudUrls: plf?.binaryPointCloudUrls,
    PointCloudFormat: plf?.pointCloudFormat,
    NumberOfPoints: plf?.numberOfPoints,
    FilteredGround: plf?.filteredGround?.toString(),
    Failed: plf?.failed,
    IsTest: plf?.isTest?.toString(),
    ProcessedAttachmentUrl: plf && (
      <a href={plf.processedAttachmentUrl}>{plf.processedAttachmentUrl}</a>
    ),
    MirroredAttachmentUrl: plf && (
      <a href={plf.mirroredAttachmentUrl}>{plf.mirroredAttachmentUrl}</a>
    ),
    OriginalAttachmentUrl: plf && (
      <a href={plf.originalAttachmentUrl}>{plf.originalAttachmentUrl}</a>
    ),
  };
}

function payableDetails(payable: $FIXME) {
  const { worker, resource, createdAt, lastUpdated, type } = payable;
  if (type === 'error') {
    return {
      Worker: lookupLink(worker, INamedObjectTypes.User),
      CreatedAt: formatDateWithTz(createdAt),
      UpdatedAt: lastUpdated,
    };
  }
  return {
    Worker: lookupLink(worker, INamedObjectTypes.User),
    Type: type,
    [`${type}`]: lookupLink(resource),
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: lastUpdated,
  };
}

function payoutDetails(payout: $FIXME) {
  const { worker, sendCents, startDate, endDate, payoutState, payoutBatchId, payoutJobId } = payout;

  return {
    PayoutJobId: payoutJobId,
    PayoutBatchId: payoutBatchId,
    SendCents: sendCents,
    Worker: lookupLink(worker, INamedObjectTypes.User),
    State: payoutState,
    StartDate: startDate,
    EndDate: endDate,
  };
}

function taskPipelineExecutionDetails(exec: HydratedPipelineExecution) {
  const { id, taskId, createdAt, updatedAt, status, owningNode } = exec;
  const details: Record<string, React.ReactNode> = {
    Task: taskId && lookupLink(taskId, INamedObjectTypes.Task),
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
    status,
    Temporal: temporalLink({
      searchQuery: id,
      workflowType: 'execution',
      temporalCloudNamespace: id.endsWith(PipelineV3NewSuffix)
        ? TemporalCloudNamespaces.TaskPipelineNew
        : undefined,
    }),
    OwningNode: lookupLink(owningNode),
  };
  return details;
}

// eslint-disable-next-line complexity
function taskPipelineNodeDetails(node: $FIXME, remoAdminCtx: $FIXME) {
  const {
    id,
    nodeName,
    type,
    status,
    params,
    error,
    projectId,
    input,
    taskId,
    createdAt,
    updatedAt,
    invalidatedAt,
    executionMeta,
    pipelineExecutions,
    generatedResources,
    possibleIntermediateError,
    possibleStackTrace,
    pipelineV3HumanNode,
    isRootTaskNode,
    substageDefinitionId,
    taskParamsMismatch,
    pipelineParamsMismatch,
    originalNextRoutingNodeIds,
  } = node;
  const details: Record<string, React.ReactNode> = {
    Name: nodeName,
    Type: type,
    Status: status,
    PipelineHumanNode: pipelineV3HumanNode && lookupLink(pipelineV3HumanNode._id),
    PipelineHumanNodeStatus: pipelineV3HumanNode && pipelineV3HumanNode.status,
    TaskParamsAreMismatched: JSON.stringify(taskParamsMismatch),
    PipelineParamsAreMismatched: JSON.stringify(pipelineParamsMismatch),
    ReviewLevel: pipelineV3HumanNode && pipelineV3HumanNode.reviewLevel,
    AttemptedBy: pipelineV3HumanNode?.attempters &&
      pipelineV3HumanNode.status === PipelineV3HumanNodeStatus.Completed && (
        <SmartLink
          key={_.last(pipelineV3HumanNode.attempters)!}
          type={INamedObjectTypes.User}
          id={_.last(pipelineV3HumanNode.attempters)!}
        ></SmartLink>
      ),
    AssignedWorker:
      (pipelineV3HumanNode?.worker && (
        <SmartLink
          key={pipelineV3HumanNode?.worker}
          type={INamedObjectTypes.User}
          id={pipelineV3HumanNode?.worker}
        ></SmartLink>
      )) ||
      'None',
    AssignedWorkerHasExclusiveClaim: JSON.stringify(!!pipelineV3HumanNode?.exclusiveWorker),
    Attempt: pipelineV3HumanNode?.attempt &&
      pipelineV3HumanNode.status === PipelineV3HumanNodeStatus.Completed && (
        <SmartLink
          key={pipelineV3HumanNode.attempt}
          type={INamedObjectTypes.TaskAttempt}
          id={pipelineV3HumanNode.attempt}
        ></SmartLink>
      ),
    AttemptToReview: pipelineV3HumanNode?.attemptToReview && (
      <SmartLink
        key={pipelineV3HumanNode?.attemptToReview}
        type={INamedObjectTypes.TaskAttempt}
        id={pipelineV3HumanNode?.attemptToReview}
      ></SmartLink>
    ),
    ClaimedUntil:
      pipelineV3HumanNode?.worker &&
      pipelineV3HumanNode?.claimedUntil &&
      formatDateWithTz(pipelineV3HumanNode?.claimedUntil),
    MaxClaim:
      pipelineV3HumanNode?.worker &&
      pipelineV3HumanNode?.maxClaim &&
      formatDateWithTz(pipelineV3HumanNode?.maxClaim),
    Params: params,
    Error: error?.message,
    ErrorStackTrace: error?.stackTrace,
    Project: projectId && !remoAdminCtx.hidePII && lookupLink(projectId, INamedObjectTypes.Project),
    Task: taskId && lookupLink(taskId, INamedObjectTypes.Task),
    SubstageDefinition: substageDefinitionId && lookupLink(substageDefinitionId),
    SubstageInstance: input?.substageInstanceId && lookupLink(input.substageInstanceId),
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
    InvalidatedAt: invalidatedAt,
    ExecutionMeta: JSON.stringify(executionMeta),
    PipelineExecutions:
      pipelineExecutions && pipelineExecutions.map((exec: any) => lookupLink(exec.id)),
    GeneratedResources:
      generatedResources && generatedResources.map((res: any) => lookupLink(res.id)),
    Temporal: temporalLink({
      searchQuery: id,
      workflowType: type,
      temporalCloudNamespace: id.endsWith(PipelineV3NewSuffix)
        ? TemporalCloudNamespaces.TaskPipelineNew
        : undefined,
    }),
    IntermediateError: possibleIntermediateError,
    StackTrace: possibleStackTrace,
    RootTaskNode: isRootTaskNode?.toString(),
    [originalNextRoutingNodeIds && originalNextRoutingNodeIds.length > 1
      ? 'OriginalNextRoutingNodes'
      : 'OriginalNextRoutingNode']:
      originalNextRoutingNodeIds && originalNextRoutingNodeIds.map((id: string) => lookupLink(id)),
  };
  return details;
}

function taskPipelineResourceDetails(resource: HydratedPipelineResource) {
  const { name, sourcePipelineNodeId, createdAt, updatedAt, outputOf, inputTo } = resource;
  const details: Record<string, React.ReactNode> = {
    Name: name,
    OutputOfNodes: outputOf && outputOf.length ? outputOf.map(n => lookupLink(n.id)) : undefined,
    InputToNodes: inputTo && inputTo.length ? inputTo.map(n => lookupLink(n.id)) : undefined,
    SourcePipelineNode: sourcePipelineNodeId && lookupLink(sourcePipelineNodeId),
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
    Temporal:
      sourcePipelineNodeId &&
      temporalLink({
        searchQuery: sourcePipelineNodeId,
        workflowType: 'node',
        temporalCloudNamespace: sourcePipelineNodeId.endsWith(PipelineV3NewSuffix)
          ? TemporalCloudNamespaces.TaskPipelineNew
          : undefined,
      }),
  };
  return details;
}

function taskPipelineManualInterventionDetails(manualIntervention: $FIXME) {
  const {
    id,
    entityId,
    subjectExecution,
    status,
    temporalWorkflowId,
    temporalRunId,
    createdAt,
    updatedAt,
    parentBulkWorkflowId,
    parentBulkWorkflowRunId,
    traitName,
    source,
    operatorEmail,
    parentCorpJob,
    isolated,
  } = manualIntervention;
  const details: Record<string, React.ReactNode> = {
    EntityId: lookupLink(entityId),
    Warning: isolated
      ? linkWithText(
          `Intervention running on isolation queue`,
          `https://scale.atlassian.net/wiki/spaces/EPD/pages/1204125698/Running+PipelineV3+BulkJobs#Mitigation-of-%E2%80%9CBulk-Job-Spam%E2%80%9D`,
        )
      : undefined,
    SubjectExecution: lookupLink(subjectExecution),
    ParentCorpJob: parentCorpJob && lookupLink(parentCorpJob),
    Workflow: directTemporalLink({
      workflowId: temporalWorkflowId,
      runId: temporalRunId,
      text: 'Workflow History',
      temporalCloudNamespace: id.endsWith(PipelineV3NewSuffix)
        ? TemporalCloudNamespaces.TaskPipelineNew
        : TemporalNamespace.LegacyCloud,
    }),
    ParentWorkflow:
      parentBulkWorkflowId &&
      parentBulkWorkflowRunId &&
      directTemporalLink({
        workflowId: parentBulkWorkflowId,
        runId: parentBulkWorkflowRunId,
        text: 'Parent Workflow History',
        temporalCloudNamespace: id.endsWith(PipelineV3NewSuffix)
          ? TemporalCloudNamespaces.TaskPipelineNew
          : TemporalNamespace.LegacyCloud,
      }),
    DelayedWorkflow: directTemporalLink({
      workflowId: `scheduled-interventions-${id}`,
      runId: undefined,
      text: 'Delayed Workflow History',
      temporalCloudNamespace: id.endsWith(PipelineV3NewSuffix)
        ? TemporalCloudNamespaces.TaskPipelineNew
        : TemporalNamespace.LegacyCloud,
    }),
    Trait: traitName,
    Status: status,
    Source: source,
    Operator: lookupLink(operatorEmail),
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
  };
  if (!_.isEmpty(manualIntervention.error)) {
    details.Error = manualIntervention.error.message;
  }
  return details;
}

function auditBatchDetails(resource: $FIXME) {
  const { auditName, auditedBy, project, createdAt, updatedAt } = resource;
  const details: Record<string, React.ReactNode> = {
    Name: auditName,
    Project: project && <SmartLink type={INamedObjectTypes.Project} id={project} />,
    AuditedBy: auditedBy && <SmartLink type={INamedObjectTypes.User} id={auditedBy} />,
    CreatedAt: formatDateWithTz(createdAt),
    UpdatedAt: formatDateWithTz(updatedAt),
  };
  return details;
}

function workLogDetails(resource: IWorkLogJson) {
  const {
    worker,
    startTime,
    endTime,
    description,
    project,
    payoutAmountHundredthsOfCents,
    approved,
    workId,
    workSource,
    date,
    workAuthority,
    approvalRecord,
    durationSeconds,
    freezeReason,
    cancelReason,
    type,
    createdAt,
  } = resource;
  const details: Record<string, React.ReactNode> = {
    Worker: <SmartLink type={INamedObjectTypes.User} id={worker} />,
    WorkType: type,
    Project: project && <SmartLink type={INamedObjectTypes.Project} id={project} />,
    Payout: `$${(payoutAmountHundredthsOfCents / 100 / 100).toFixed(2)}`,
    ApprovalStatus: approved,
    StartTime: startTime && formatDateWithTz(startTime),
    EndTime: endTime && formatDateWithTz(endTime),
    DurationSeconds: durationSeconds,
    CreatedAt: formatDateWithTz(createdAt),
    Description: description,
    WorkId: workId,
    WorkSource: workSource,
    Date: date && formatDateWithTz(date),
    WorkAuthority: workAuthority,
    approvalRecord: approvalRecord && (
      <a href={'/corp/review/' + approvalRecord}>{approvalRecord}</a>
    ),
    FreezeReason: freezeReason,
    CancelReason: cancelReason,
  };
  return details;
}

function pipelineV3HumanNodeDetails(
  pipelineV3HumanNode: PipelineV3HumanNodeCorpLookup,
  remoAdminCtx: $FIXME,
) {
  const {
    attempt,
    attemptToReview,
    claimedUntil,
    maxClaim,
    node,
    project,
    reviewLevel,
    status,
    task,
    worker,
  } = pipelineV3HumanNode;
  const details: Record<string, React.ReactNode> = {
    Task: lookupLink(task._id, INamedObjectTypes.Task),
    Project: (
      <span>
        {' '}
        {project.name}{' '}
        {!remoAdminCtx.isRemoAdmin
          ? lookupLink(project._id, INamedObjectTypes.Project)
          : `| ${project._id}`}
      </span>
    ),
    ReviewLevel: reviewLevel,
    Status: status,
    Node: lookupLink(node),
    Attempt: attempt && lookupLink(attempt._id, INamedObjectTypes.TaskAttempt),
    AttemptToReview:
      attemptToReview && lookupLink(attemptToReview._id, INamedObjectTypes.TaskAttempt),
    AssignedWorker: (worker && lookupLink(worker, INamedObjectTypes.User)) || 'None',
    ClaimedUntil: worker && claimedUntil && formatDateWithTz(claimedUntil),
    MaxClaim: worker && maxClaim && formatDateWithTz(maxClaim),
    Temporal: temporalLink({
      searchQuery: node,
      workflowType: 'human',
      temporalCloudNamespace: node.endsWith(PipelineV3NewSuffix)
        ? TemporalCloudNamespaces.TaskPipelineNew
        : undefined,
    }),
  };
  return details;
}

function qaOperationNodeDetails(
  node: QAOperationNodePopulatedOperationJson,
  _remoAdminCtx: IRemoAdminContext,
) {
  const {
    qaOperation,
    type,
    status,
    assignedTo,
    createdAt,
    updatedAt,
    payoutAmountHundredthsOfCents,
  } = node;
  const { dueDate, project, name } = qaOperation;
  const timeSpentSeconds =
    type === QAOperationNodeType.Attempt
      ? qaOperation.attemptTimeSpent
      : qaOperation.reviewTimeSpent;
  const timeSpent = timeSpentSeconds ? durationToString(timeSpentSeconds) : undefined;
  const maxTimeRequired = qaOperation.maxTimeRequired
    ? durationToString(qaOperation.maxTimeRequired)
    : undefined;
  const payableId = qaOperation.stateMachine.context?.payableId;

  const details: Record<string, React.ReactNode> = {
    qaOperation: <RemoAdminLookupLink id={qaOperation._id} />,
    name,
    project,
    // @ts-expect-error @types/react@18 $FIXME Date is not a ReactNode
    dueDate,
    type,
    status,
    assignedTo,
    // @ts-expect-error @types/react@18 $FIXME Date is not a ReactNode
    createdAt,
    // @ts-expect-error @types/react@18 $FIXME Date is not a ReactNode
    updatedAt,
    maxTimeRequired,
    timeSpent,
    payoutAmountHundredthsOfCents,
    payable: payableId ? <RemoAdminLookupLink id={payableId} /> : undefined,
  };
  return details;
}

function qaOperationDetails(qaOperation: QAOperationJson, _remoAdminCtx: IRemoAdminContext) {
  const { dueDate, stateMachine, project, name } = qaOperation;
  const { attemptNodeId, reviewNodeId, gate } = stateMachine.context;
  const maxTimeRequired = qaOperation.maxTimeRequired
    ? durationToString(qaOperation.maxTimeRequired)
    : undefined;
  const attemptTimeSpent = qaOperation.attemptTimeSpent
    ? durationToString(qaOperation.attemptTimeSpent)
    : undefined;
  const reviewTimeSpent = qaOperation.reviewTimeSpent
    ? durationToString(qaOperation.reviewTimeSpent)
    : undefined;
  const payableId = qaOperation.stateMachine.context?.payableId;

  const details: Record<string, React.ReactNode> = {
    name,
    project,
    // @ts-expect-error @types/react@18 $FIXME Date is not a ReactNode
    dueDate,
    gate,
    maxTimeRequired,
    attemptNodeId: <RemoAdminLookupLink id={attemptNodeId} />,
    attemptTimeSpent,
    reviewNodeId: reviewNodeId ? <RemoAdminLookupLink id={reviewNodeId} /> : undefined,
    reviewTimeSpent,
    payable: payableId ? <RemoAdminLookupLink id={payableId} /> : undefined,
  };
  return details;
}

function crcOperationDetails(crcOperation: CrCOperationJson) {
  const { date, assignedTo, status, crcWorkerId, payable, project } = crcOperation;

  const details: Record<string, React.ReactNode> = {
    Worker: assignedTo ? <RemoAdminLookupLink id={assignedTo} /> : undefined,
    CrcWorkerId: crcWorkerId,
    Status: status,
    Payable: payable ? <RemoAdminLookupLink id={payable} /> : undefined,
    Project: project,
    // @ts-expect-error @types/react@18 $FIXME Date is not a ReactNode
    Date: date,
  };

  return details;
}

function hubstaffOperationDetails(hubstaffOperation: HubstaffOperationJson) {
  const { date, assignedTo, status, hubstaffProjectId, payable, project } = hubstaffOperation;

  const details: Record<string, React.ReactNode> = {
    Worker: assignedTo ? <RemoAdminLookupLink id={assignedTo} /> : undefined,
    HubstaffProjectID: hubstaffProjectId,
    Status: status,
    Payable: payable ? <RemoAdminLookupLink id={payable} /> : undefined,
    Project: project,
    Date: date,
  };

  return details;
}

function incompleteTaskAttemptDetails(incompleteTaskAttempt: $FIXME) {
  const {
    project,
    task,
    subtask,
    attemptedAtReviewLevel,
    attemptedAt,
    worker,
    payable,
    pipelineV3HumanNode,
    pipelineV3Node,
  } = incompleteTaskAttempt;

  const formatReviewLevel = getSanitizedReviewLevel(attemptedAtReviewLevel);

  return {
    Project: project && lookupLink(project, INamedObjectTypes.Project),
    Task: task && lookupLink(task._id, INamedObjectTypes.Task),
    Subtask: subtask && lookupLink(subtask._id, INamedObjectTypes.Subtask),
    PipelineV3Node: pipelineV3Node && lookupLink(pipelineV3Node),
    PipelineHumanNode: pipelineV3HumanNode && lookupLink(pipelineV3HumanNode),
    'Attempted at': attemptedAt,
    'Attempted by': lookupLink(worker, INamedObjectTypes.User),
    'Attempted at Review level': formatReviewLevel,
    Payable: lookupLink(payable),
  };
}

function otsDeliveredBatchDetails(otsDeliveredBatch: DeliveredBatchLookupDTO) {
  const {
    _id,
    _project,
    _otsSubscription,
    createdAt,
    deliveredAt,
    status,
    note,
    datapackType,
    isSample,
    _dataStream,
  } = otsDeliveredBatch;
  const isLocal = process.env.NODE_ENV === 'development';
  const consoleHref = isLocal
    ? `http://localhost:3003/data-gallery/deliveries/${_id}`
    : `https://console.scale.com/data-gallery/deliveries/${_id}`;
  const consoleLink = (
    <a href={consoleHref} target="_blank" rel="noopener noreferrer">
      Link
    </a>
  );
  return {
    'OTS Subscription': _otsSubscription && (
      <span>
        {_otsSubscription.customerCodename}{' '}
        {lookupLink(_otsSubscription._id, INamedObjectTypes.OTSSubscription)}
      </span>
    ),
    Project: _project && (
      <span>
        {_project.name} {lookupLink(_project._id, INamedObjectTypes.Project)}
      </span>
    ),
    Status: (
      <Badge color={OTS_DELIVERY_STATUS_TO_COLOR[status as OTSDeliveredBatchStatus]}>
        {_.capitalize(status)}
      </Badge>
    ),
    Console: consoleLink,
    DatapackType: `${datapackType} (${_dataStream.name})`,
    DeliveryNote: note,
    IsSample: `${isSample}`,
    CreatedAt: createdAt ? formatDateWithTz(createdAt) : undefined,
    DeliveredAt: deliveredAt ? formatDateWithTz(deliveredAt) : undefined,
  };
}

function otsDeliveredTaskDetails(otsDeliveredTask: OTSDeliveredTaskLookupDTO) {
  const {
    task,
    otsTask,
    customer,
    status,
    createdAt,
    acceptedAt,
    datapackType,
    _otsDeliveredBatch,
    _dataStream,
  } = otsDeliveredTask;
  return {
    OTSDeliveredBatch:
      _otsDeliveredBatch && lookupLink(_otsDeliveredBatch._id, INamedObjectTypes.OTSDeliveredBatch),
    Task: lookupLink(task, INamedObjectTypes.Task),
    OTSTask: lookupLink(otsTask, INamedObjectTypes.OTSTask),
    Customer: lookupLink(customer, INamedObjectTypes.User),
    DatapackType: `${datapackType} (${_dataStream.name})`,
    Status: status,
    CreatedAt: createdAt ? formatDateWithTz(createdAt) : undefined,
    AcceptedAt: acceptedAt ? formatDateWithTz(acceptedAt) : undefined,
  };
}

function otsSubscriptionDetails(otsSubscription: SubscriptionLookupDTO) {
  const { customer, customerName, customerCodename } = otsSubscription;
  return {
    Customer: customer && lookupLink(customer, INamedObjectTypes.User),
    CustomerName: customerName,
    CustomerCodename: customerCodename,
  };
}

function otsTaskDetails(otsTask: OTSTaskLookupDTO) {
  const {
    task,
    uploadBatch,
    uploadBatchIds,
    status,
    datapackType,
    createdAt,
    updatedAt,
    _dataStream,
  } = otsTask;

  const uploadBatchId =
    uploadBatchIds && uploadBatchIds.length > 0
      ? uploadBatchIds[uploadBatchIds.length - 1]
      : uploadBatch;
  return {
    Task: lookupLink(task, INamedObjectTypes.Task),
    UploadBatch: uploadBatchId && lookupLink(uploadBatchId, INamedObjectTypes.OTSUploadBatch),
    Status: status,
    DatapackType: `${datapackType} (${_dataStream.name})`,
    CreatedAt: createdAt ? formatDateWithTz(createdAt) : undefined,
    UpdatedAt: updatedAt ? formatDateWithTz(updatedAt) : undefined,
  };
}

function otsUploadBatchDetails(otsUploadBatch: OTSUploadBatchLookupDTO) {
  const { uploadedBy, createdAt, note, datapackType, _project, _dataStream } = otsUploadBatch;
  return {
    UploadedBy: uploadedBy && lookupLink(uploadedBy, INamedObjectTypes.User),
    Project: _project && (
      <span>
        {_project.name} {lookupLink(_project._id, INamedObjectTypes.Project)}
      </span>
    ),
    DatapackType: `${datapackType} (${_dataStream.name})`,
    CreatedAt: createdAt ? formatDateWithTz(createdAt) : undefined,
    Note: note,
  };
}

function modelReqAndRespDetails(
  modelReqAndResp: ModelRequestAndResponseLookupDTO,
): Record<string, React.ReactNode> {
  let detailedErrorMessage: undefined | string = undefined;
  if (modelReqAndResp.response) {
    detailedErrorMessage = modelReqAndResp.response;
    const parsedResp = JSON.parse(modelReqAndResp.response);
    if (parsedResp.error?.message) {
      detailedErrorMessage = parsedResp.error?.message;
    }
  }

  return {
    Customer:
      modelReqAndResp.customerId && lookupLink(modelReqAndResp.customerId, INamedObjectTypes.User),
    Project:
      modelReqAndResp.projectId && lookupLink(modelReqAndResp.projectId, INamedObjectTypes.Project),
    Task: modelReqAndResp.taskId && lookupLink(modelReqAndResp.taskId, INamedObjectTypes.Task),
    'Pipeline V3 Human Node': modelReqAndResp.assignmentId, // && lookupLink(modelReqAndResp.pipelineV3HumanNode, INamedObjectTypes.TaskPipelineNode)
    'Attempted by':
      modelReqAndResp.contributorId &&
      lookupLink(modelReqAndResp.contributorId, INamedObjectTypes.User),
    ModelName: modelReqAndResp.modelName,
    URL: typeof modelReqAndResp?.request === 'object' && modelReqAndResp?.request?.url,
    StatusCode: modelReqAndResp.statusCode,
    ErrorMessage: modelReqAndResp.errorMessage,
    DetailedErrorMessage: detailedErrorMessage,
    'Latency (ms)': modelReqAndResp.latency,
    CreatedAt: modelReqAndResp.createdAt && formatDateWithTz(modelReqAndResp.createdAt),
  };
}

// eslint-disable-next-line complexity
function getTypeProps(
  type: string,
  object: $FIXME,
  remoAdminCtx: IRemoAdminContext,
): Record<string, React.ReactNode> | null {
  if (type === 'subtask' || type === 'task' || type === 'testtask') {
    return taskDetails(object, remoAdminCtx);
  } else if (type === 'taskattempt') {
    return taskAttemptDetails(object);
  } else if (type === 'taskreview') {
    return taskReviewDetails(object);
  } else if (type === 'user') {
    return userDetails(object);
  } else if (type === 'project') {
    return projectDetails(object);
  } else if (type === 'taskpermissionentry') {
    return permissionEntryDetails(object);
  } else if (type === 'taskpermissiongroup') {
    return permissionGroupDetails(object);
  } else if (type === 'trainingscenario') {
    return trainingScenarioDetails(object);
  } else if (type === 'trainingattempt') {
    return trainingAttemptDetails(object);
  } else if (type === 'course') {
    return courseDetails(object);
  } else if (type === 'customeraudit') {
    return customerAuditDetails(object);
  } else if (type === 'tag') {
    return tagDetails(object);
  } else if (type === 'taskbatch') {
    return batchDetails(object);
  } else if (type === 'chatdeliverybatch') {
    return chatDeliveryBatchDetails(object);
  } else if (type === 'pipelineStage') {
    return pipelineStages(object);
  } else if (type === 'corppermissionrole') {
    return corpPermissionRoleDetails(object);
  } else if (type === 'contract') {
    return contractDetails(object);
  } else if (type === 'instructionitem') {
    return instructionItemDetails(object);
  } else if (type === 'adminaction') {
    return adminActionDetails(object);
  } else if (type === 'auditplus') {
    return auditPlusDetails(object);
  } else if (type === 'cuboidpath') {
    return cuboidPathDetails(object);
  } else if (type === 'corpjob') {
    return corpJobDetails(object);
  } else if (type === 'ontology') {
    return ontologyDetails(object);
  } else if (type === 'testproject') {
    return testProjectDetails(object);
  } else if (type === 'processedlidarframe') {
    return plfDetails(object);
  } else if (type === 'payable') {
    return payableDetails(object);
  } else if (type === 'payout') {
    return payoutDetails(object);
  } else if (type === TaskPipelineObject.TaskPipelineExecution) {
    return taskPipelineExecutionDetails(object);
  } else if (type === TaskPipelineObject.TaskPipelineNode) {
    return taskPipelineNodeDetails(object, remoAdminCtx);
  } else if (type === TaskPipelineObject.TaskPipelineResource) {
    return taskPipelineResourceDetails(object);
  } else if (type === TaskPipelineObject.TaskPipelineManualIntervention) {
    return taskPipelineManualInterventionDetails(object);
  } else if (type === 'auditbatch') {
    return auditBatchDetails(object);
  } else if (type === 'worklog') {
    return workLogDetails(object);
  } else if (type === 'pipelinev3humannode') {
    return pipelineV3HumanNodeDetails(object, remoAdminCtx);
  } else if (type === 'qaoperation') {
    return qaOperationDetails(object, remoAdminCtx);
  } else if (type === 'qaoperationnode') {
    return qaOperationNodeDetails(object, remoAdminCtx);
  } else if (type === 'crcoperation') {
    return crcOperationDetails(object);
  } else if (type === 'hubstaffoperation') {
    return hubstaffOperationDetails(object);
  } else if (type === 'incompletetaskattempt') {
    return incompleteTaskAttemptDetails(object);
  } else if (type === 'otsDeliveredBatch') {
    return otsDeliveredBatchDetails(object);
  } else if (type === 'otsDeliveredTask') {
    return otsDeliveredTaskDetails(object);
  } else if (type === 'otsSubscription') {
    return otsSubscriptionDetails(object);
  } else if (type === 'otsTask') {
    return otsTaskDetails(object);
  } else if (type === 'otsUploadBatch') {
    return otsUploadBatchDetails(object);
  } else if (type === 'modelRequestAndResponse') {
    return modelReqAndRespDetails(object);
  }
  return null;
}

function getSharedProps(object: $FIXME) {
  const datadogHref = `https://app.datadoghq.com/logs?query=${object._id}`;
  const datadogLink = (
    <a href={datadogHref} target="_blank" rel="noopener noreferrer">
      Logs
    </a>
  );
  return {
    Datadog: datadogLink,
  };
}

export default function ObjectDetails(object: $FIXME) {
  const remoAdminCtx = useRemoAdminCtx();
  const type = object && object.__t;
  const sharedProps = getSharedProps(object);
  const typeProps = getTypeProps(type, object, remoAdminCtx);
  const idProps = object.withId
    ? {
        EntityType: object.__t,
        Id: (
          <span>
            <LookupObjectLink objectId={object._id} />
          </span>
        ),
      }
    : {};

  return (
    <div>
      <DetailsTable {...idProps} {...typeProps} {...sharedProps} />
    </div>
  );
}
